import React, { useEffect } from 'react';
import {
    RefreshCcw, Mail,
    CheckCircle2, AlertCircle
} from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { SEOHead, SEO_META_TAGS, useSEO } from '@/seo';

const Refunds = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const timelineSteps = [
        { label: "Review", time: "2-3 Business Days" },
        { label: "Approval", time: "1 Business Day" },
        { label: "Refund", time: "5-7 Business Days" }
    ];

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-white relative">
            <SEOHead {...useSEO(SEO_META_TAGS.refunds.English, 'English')} />
            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-32 md:pb-24 px-4 sm:px-6 font-sans">
                <div className="container mx-auto max-w-5xl">

                    <PublicPageHeader
                        title="Cancellations & Refunds"
                        highlightedText="Refunds"
                        description="Our policies for cancellations, refunds, and fair resolutions."
                        icon={RefreshCcw}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-24 items-start">
                        <section
                            className="bg-white/[0.08] border border-white/20 rounded-[40px] p-10 group hover:border-purple-400/50 transition-colors duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
                        >
                            <h2 className="text-3xl font-bold mb-10 text-white flex items-center gap-6 font-serif tracking-tight">
                                <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                    <CheckCircle2 size={28} />
                                </div>
                                Eligible Refunds
                            </h2>
                            <ul className="space-y-8 pl-2">
                                <li className="flex gap-5 group/item">
                                    <div className="w-2 h-2 rounded-full bg-purple-400 mt-2.5 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.5)] group-hover/item:scale-150 transition-transform" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Unused wallet credits</strong>
                                        <span className="text-base text-purple-100/80 font-medium leading-relaxed">Full refund available within 7 days of purchase.</span>
                                    </div>
                                </li>
                                <li className="flex gap-5 group/item">
                                    <div className="w-2 h-2 rounded-full bg-purple-400 mt-2.5 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.5)] group-hover/item:scale-150 transition-transform" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Technical issues</strong>
                                        <span className="text-base text-purple-100/80 font-medium leading-relaxed">Platform unavailability during active sessions.</span>
                                    </div>
                                </li>
                                <li className="flex gap-5 group/item">
                                    <div className="w-2 h-2 rounded-full bg-purple-400 mt-2.5 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.5)] group-hover/item:scale-150 transition-transform" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Duplicate payments</strong>
                                        <span className="text-base text-purple-100/80 font-medium leading-relaxed">Accidental multiple payments for a single service.</span>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        <section
                            className="bg-white/[0.08] border border-white/20 rounded-[40px] p-10 group hover:border-red-500/30 transition-colors duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
                        >
                            <h2 className="text-3xl font-bold mb-10 text-white flex items-center gap-6 font-serif tracking-tight">
                                <div className="w-14 h-14 rounded-2xl bg-red-500/5 border border-red-500/10 flex items-center justify-center text-red-400">
                                    <AlertCircle size={28} />
                                </div>
                                Non-Eligible
                            </h2>
                            <ul className="space-y-8 pl-2">
                                <li className="flex gap-5 group/item opacity-70 hover:opacity-100 transition-opacity">
                                    <div className="w-2 h-2 rounded-full bg-red-400/60 mt-2.5 flex-shrink-0" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Used services</strong>
                                        <span className="text-base text-purple-100/80 leading-relaxed font-medium">Credits already used for consultations.</span>
                                    </div>
                                </li>
                                <li className="flex gap-5 group/item opacity-70 hover:opacity-100 transition-opacity">
                                    <div className="w-2 h-2 rounded-full bg-red-400/60 mt-2.5 flex-shrink-0" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Subjective outcomes</strong>
                                        <span className="text-base text-purple-100/80 leading-relaxed font-medium">Interpretations may vary based on perspective.</span>
                                    </div>
                                </li>
                                <li className="flex gap-5 group/item opacity-70 hover:opacity-100 transition-opacity">
                                    <div className="w-2 h-2 rounded-full bg-red-400/60 mt-2.5 flex-shrink-0" />
                                    <div>
                                        <strong className="text-white block font-serif text-lg mb-2">Incorrect input</strong>
                                        <span className="text-base text-purple-100/80 leading-relaxed font-medium">Wrong birth details provided by the user.</span>
                                    </div>
                                </li>
                            </ul>
                        </section>
                    </div>

                    <section className="mb-24 p-16 bg-white/[0.08] rounded-[56px] border border-white/20 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                        <div className="absolute top-0 right-0 p-16 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity -rotate-12 group-hover:rotate-0 duration-1000">
                            <RefreshCcw size={240} className="text-purple-500" />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-4xl font-black mb-16 text-center font-serif tracking-tight text-white">Processing Cycle</h2>
                            <div className="flex flex-col md:flex-row justify-between items-center max-w-4xl mx-auto gap-12">
                                {timelineSteps.map((step, i) => (
                                    <React.Fragment key={i}>
                                        <div className="flex flex-col items-center group/step text-center">
                                            <div className="w-20 h-20 rounded-[28px] bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-6 font-black text-2xl group-hover/step:bg-purple-500/20 transition-colors duration-500 shadow-[0_0_20px_rgba(147,51,234,0.1)]">
                                                {i + 1}
                                            </div>
                                            <h4 className="text-xl font-bold text-white mb-3 font-serif">{step.label}</h4>
                                            <p className="text-xs text-purple-200/70 font-sans tracking-widest uppercase font-bold">{step.time}</p>
                                        </div>
                                        {i < timelineSteps.length - 1 && (
                                            <div className="hidden md:block h-[1px] w-32 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent flex-shrink" />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </section>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
                        <div
                            className="p-12 bg-white/[0.08] border border-white/20 rounded-[48px] group hover:border-purple-500/20 transition-colors duration-500"
                        >
                            <h3 className="text-3xl font-bold mb-10 flex items-center gap-6 font-serif text-white tracking-tight">
                                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                    <Mail size={24} />
                                </div>
                                Initiating Request
                            </h3>
                            <div className="space-y-8 pl-2">
                                <p className="text-purple-100/80 leading-relaxed text-lg font-sans">Email us at <strong className="text-white">support@bhrigugpt.com</strong> with:</p>
                                <ul className="space-y-5">
                                    {[
                                        "Your account email address",
                                        "Transaction ID or Order number",
                                        "Reason for refund request",
                                        "Supporting evidence if applicable"
                                    ].map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-4 text-purple-100/90 font-medium font-sans text-base">
                                            <div className="w-7 h-7 rounded-full bg-purple-500/10 flex items-center justify-center text-xs text-purple-400 border border-purple-500/20 font-bold flex-shrink-0 mt-0.5">
                                                {idx + 1}
                                            </div>
                                            <span className="leading-relaxed">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <a href="mailto:support@bhrigugpt.com" className="w-full h-16 bg-white text-gray-900 rounded-[28px] font-bold font-sans uppercase tracking-widest text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform shadow-xl mt-6 relative overflow-hidden group/btn flex items-center justify-center">
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-white opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                                    <span className="relative z-10">Request Refund</span>
                                </a>
                            </div>
                        </div>

                        <div
                            className="p-12 bg-white/[0.08] border border-white/20 rounded-[48px] group hover:border-purple-500/20 transition-colors duration-500 flex flex-col justify-between"
                        >
                            <div>
                                <h3 className="text-3xl font-bold mb-10 flex items-center gap-6 font-serif text-white tracking-tight">
                                    <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                        <RefreshCcw size={24} />
                                    </div>
                                    Appeal Process
                                </h3>
                                <p className="font-sans text-purple-100/80 leading-relaxed mb-12 text-lg pl-1">
                                    Not satisfied with the initial decision? You may request a senior review. Our team will re-evaluate within 5-7 business days.
                                </p>
                            </div>
                            <div className="p-10 bg-purple-500/5 rounded-[40px] border border-purple-500/10 flex items-start gap-6 group/note backdrop-blur-sm">
                                <AlertCircle size={28} className="text-purple-400 mt-1 flex-shrink-0" />
                                <p className="text-base text-purple-200/80 leading-relaxed font-medium font-sans">
                                    Goodwill refunds may occasionally be offered to first-time users who experience genuine issues with their consultation.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </main>

            <Footer />
            <MobileBottomNav />
        </div>
    );
};

export default Refunds;
